import React, { useState, useEffect } from 'react';
import { stocktakeSessionsService } from '../../services';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';

export function ValidationReportPanel({ sessionId, validationReport, onRefresh, isOfflineMode, onRunLocalValidation }) {
  const [report, setReport] = useState(validationReport || null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (validationReport) setReport(validationReport);
  }, [validationReport]);

  const handleRunValidation = async () => {
    setLoading(true);
    try {
      if (isOfflineMode && onRunLocalValidation) {
        const result = onRunLocalValidation();
        setReport(result);
        if (onRefresh) onRefresh(result);
        const desc = result.valid && !result.warning_count
          ? 'All checks passed.'
          : `${result.error_count} error(s), ${result.warning_count} warning(s)`;
        toast({ title: 'Local Validation Complete', description: desc });
      } else {
        const result = await stocktakeSessionsService.validateSession(sessionId);
        setReport(result);
        if (onRefresh) onRefresh(result);
        const desc = result.valid && !result.warning_count
          ? 'All checks passed.'
          : `${result.error_count} error(s), ${result.warning_count} warning(s)`;
        toast({ title: 'Validation Complete', description: desc });
      }
    } catch (err) {
      toast({ title: 'Validation Failed', description: err.response?.data?.detail || err.message || 'Error', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const severityConfig = {
    error: { label: 'Error', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: 'text-red-500', dot: 'bg-red-500' },
    warning: { label: 'Warning', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: 'text-amber-500', dot: 'bg-amber-500' },
    info: { label: 'Info', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: 'text-blue-500', dot: 'bg-blue-500' },
  };

  const categoryLabels = {
    data_integrity: 'Data Integrity',
    costing: 'Costing',
    unit_conversion: 'Unit Conversion',
    entry_quality: 'Entry Quality',
    balance: 'Balance',
    session: 'Session',
    closing_stock: 'Closing Stock',
    opening_stock: 'Opening Stock',
    yield_configuration: 'Yield Configuration',
    esr_integrity: 'ESR Integrity',
  };

  const renderIssue = (issue, idx) => {
    const cfg = severityConfig[issue.severity] || severityConfig.info;
    return (
      <div key={idx} className={`rounded-md border ${cfg.border} ${cfg.bg} px-3 py-2.5`} data-testid={`validation-issue-${issue.severity}-${idx}`}>
        <div className="flex items-start gap-2">
          <span className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${cfg.dot}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold uppercase ${cfg.text}`}>{cfg.label}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/60 text-slate-600 font-medium">
                {categoryLabels[issue.category] || issue.category}
              </span>
              {issue.product_name && (
                <span className="text-[10px] text-slate-500 truncate max-w-[200px]">{issue.product_name}</span>
              )}
            </div>
            <p className={`text-xs mt-1 ${cfg.text}`}>{issue.message}</p>
            {issue.resolution && (
              <p className="text-[10px] mt-1 text-slate-600 italic">Fix: {issue.resolution}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card data-testid="validation-report-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-indigo-600" />
            Verification & Validation
            {isOfflineMode && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border bg-purple-100 text-purple-700 border-purple-200" data-testid="data-source-local-validation-engine">
                Local Validation Engine
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {report && (
              <button
                className="text-xs text-slate-500 hover:text-slate-700 underline"
                onClick={() => setExpanded(e => !e)}
                data-testid="toggle-validation-btn"
              >
                {expanded ? 'Collapse' : 'Expand'}
              </button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleRunValidation}
              disabled={loading}
              data-testid="run-validation-btn"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              {isOfflineMode ? 'Run Local Checks' : 'Run Checks'}
            </Button>
          </div>
        </div>
        {report && (
          <CardDescription className="text-xs">
            {report.valid && !report.warning_count ? (
              <span className="text-emerald-600 font-medium">All checks passed</span>
            ) : (
              <span>
                {report.error_count > 0 && <span className="text-red-600 font-medium">{report.error_count} error(s)</span>}
                {report.error_count > 0 && report.warning_count > 0 && ', '}
                {report.warning_count > 0 && <span className="text-amber-600 font-medium">{report.warning_count} warning(s)</span>}
                {(report.error_count > 0 || report.warning_count > 0) && report.info_count > 0 && ', '}
                {report.info_count > 0 && <span className="text-blue-600">{report.info_count} info</span>}
              </span>
            )}
            {' '} | {report.total_issues} total check(s)
          </CardDescription>
        )}
      </CardHeader>
      {report && expanded && (
        <CardContent>
          {report.valid && report.total_issues === 0 && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="validation-all-pass">
              All verification and validation checks passed. The session data is consistent.
            </div>
          )}
          {report.valid && report.total_issues > 0 && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 mb-3" data-testid="validation-pass-with-info">
              No blocking errors found. Review the items below for completeness.
            </div>
          )}
          {!report.valid && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-3" data-testid="validation-has-errors">
              {report.error_count} error(s) detected that may affect calculation accuracy. Review and resolve before posting.
            </div>
          )}
          <div className="space-y-2 mt-2" data-testid="validation-issues-list">
            {(report.errors || []).map((issue, idx) => renderIssue(issue, idx))}
            {(report.warnings || []).map((issue, idx) => renderIssue(issue, idx))}
            {(report.info || []).map((issue, idx) => renderIssue(issue, idx))}
          </div>
        </CardContent>
      )}
      {!report && (
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            {isOfflineMode
              ? 'Click "Run Local Checks" or calculate the audit to generate a validation report from local data.'
              : 'Click "Run Checks" or calculate the audit to generate a validation report.'}
          </p>
        </CardContent>
      )}
    </Card>
  );
}
